import React, { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { Form, FormSpy } from 'react-final-form';
import { useLocation } from 'react-router-dom';

import { StickyButtons } from '../../molecules/StickyButtons';
import { ButtonGroup } from '../../molecules/ButtonGroup';
import { Button } from '../../atoms/Button';
import { useDropDown } from '../../../lib/useDropdown';
import { analytics } from '../../../lib/analytics';

export const MultiForm = ({ name, initialValues, children, onSubmit, initialHistory, hideButtons = false, alwaysUseButtonRows = false, ignoreStoredHistory = false, ignoreStoredValues = false, ...props }) => {
  const location = useLocation();
  const formRef = useRef(null);

  const getInitialVals = () => {
    const stored = ignoreStoredValues ? null : localStorage.getItem(`${name}-values`);
    if (stored) return JSON.parse(stored);
    return typeof initialValues === 'object' ? initialValues : {};
  };

  const getInitialHistory = () => {
    const stored = localStorage.getItem(`${name}-history`);

    // Use stored history if enabled and it exists
    if (stored && !ignoreStoredHistory) {
      const storedParsed = JSON.parse(stored);
      if (storedParsed?.length) return storedParsed;
    }

    if (initialHistory) return initialHistory;
    return [0];
  };

  const [vals, setVals] = useState(getInitialVals);
  const [history, setHistory] = useState(getInitialHistory);

  const ethnicGroups = useDropDown('ethnicity', (values) => values.filter(obj => ['4 Not Disclosed'].indexOf(obj.label) === -1));

  useEffect(() => {
    if (ignoreStoredValues) return;

    localStorage.setItem(`${name}-values`, JSON.stringify(vals));
  }, [vals]);

  useEffect(() => {
    localStorage.setItem(`${name}-history`, JSON.stringify(history));
    const lastItem = history[history.length - 1];
    analytics('pageview', {page: `${location.pathname}/${lastItem}`});
  }, [history]);

  useEffect(() => {
    const storedHistory = localStorage.getItem(`${name}-history`);
    if(storedHistory && !ignoreStoredHistory) {
      setHistory(JSON.parse(storedHistory));
    }
  }, []);

  useEffect(() => {
    const storedStr = ignoreStoredValues ? null : localStorage.getItem(`${name}-values`);
    let hasStored = false;
    if (storedStr) {
      const parsed = JSON.parse(storedStr);
      hasStored = parsed && typeof parsed === 'object' && Object.keys(parsed).length > 0;
    }
    const hasInitialValues = initialValues && typeof initialValues === 'object' && Object.keys(initialValues).length > 0;
    if (hasStored || !hasInitialValues) return;

    setVals((prev) => {
      const safePrev = prev || {};

      switch (name) {
        case 'assessment':
          return {
            ...safePrev,
            [name]: {
              motivation_other: initialValues.motivation_other,
              ethnicity: ethnicGroups.find((e) => e.value === initialValues.ethnicity),
              assessment_height_cm: initialValues.height,
              assessment_weight_kg: initialValues.weight,
              active_minutes: initialValues.active_minutes,
              sitting_minutes: initialValues.sitting_minutes,
            },
          };

        case 'SelectATime':
          return {
            ...safePrev,
            days: initialValues.days,
            times: initialValues.times,
          };

        case 'UpdateContactMethodForm':
          return {
            ...safePrev,
            contact_method: initialValues.contact_method,
          };

        default:
          return safePrev;
      }
    });
  }, [initialValues, name, ethnicGroups]);

  const next = (values, step) => {
    const currentPage = history[history.length - 1];

    const nextPage = currentPage + step;
    setHistory([...history, nextPage]);
    setVals(values);
  };

  const previous = () => {
    const newHistory = history.slice(0, history.length - 1);
    setHistory(newHistory);
  };

  const skipTo = (page) => {
    const skipPageIdx = history.indexOf(page);
    if (skipPageIdx === -1) return;

    const newHistory = history.slice(0, skipPageIdx + 1);
    setHistory(newHistory);
  };

  /**
   * NOTE: Both validate and handleSubmit switching are implemented
   * here because 🏁 Redux Final Form does not accept changes to those
   * functions once the form has been defined.
   */
  const validate = values => {
    const page = history[history.length - 1];
    const activePage = React.Children.toArray(children)[page];
    return activePage.props.validate ? activePage.props.validate(values) : {};
  };

  const handleSubmit = values => {
    const page = history[history.length - 1];
    const activePage = React.Children.toArray(children)[page];
    const isLastPage = page === React.Children.count(children) - 1;

    if (isLastPage) {
      // clear the values from local storage
      localStorage.removeItem(`${name}-values`);
      localStorage.removeItem(`${name}-history`);
      return onSubmit(values);
    } else {
      // we pass step variable through because the page next() function may change the step value
      if(activePage.props.next) {
        activePage.props.next(values, 1, next);
      } else {
        next(values, 1);
      }
    }
  };

  const page = history[history.length - 1];
  const activePage = React.Children.toArray(children)[page];
  const isLastPage = page === React.Children.count(children) - 1;

  const buttons = [];
  if (! hideButtons) {
    if(page > 0) buttons.push(<Button label='Back' mode='secondary' onClick={previous} key={1} />);
    if(!isLastPage) buttons.push(<Button label='Continue' mode='primary' isSubmit key={2} />);
    if(isLastPage) buttons.push(<Button label='Submit' mode='primary' isSubmit key={2} />);
  }

  const buttonGroup = (
    <StickyButtons>
      <ButtonGroup buttons={buttons} alwaysRow={alwaysUseButtonRows} />
    </StickyButtons>
  );

  // we inject the buttons into the child prop here.
  const kids = React.Children.toArray(activePage.props.children);
  const newChild = React.cloneElement(
    kids[0],
    {buttons: buttonGroup, skipToPage: skipTo}
  );

  return (
    <Form
      initialValues={vals}
      validate={validate}
      onSubmit={handleSubmit}
    >
      {({ handleSubmit, submitting, values }) => (
        <form onSubmit={handleSubmit} className='flex flex-col flex-grow w-full' ref={formRef}>
          {newChild}

          <FormSpy
            subscription={{ submitFailed: true, errors: true }}
            onChange={(props) => {
              const errors = props.errors.client;
              if (formRef.current && props.submitFailed && errors) {
                let fieldName = Object.keys(errors)[0];
                switch (fieldName) {
                  case 'dob':
                    fieldName = '[name="dob"]';
                    break;
                  case 'pathways':
                    fieldName = '.badge-group';
                    break;
                  default:
                    fieldName = `[name="client[${fieldName}]"]`;
                    break;
                }

                const el = formRef.current.querySelector(fieldName);
                if (el) {
                  const parentEl = el.closest('.input-container');
                  parentEl.scrollIntoView({ behavior: 'smooth' });
                }
              }
            }}
          />
        </form>
      )}
    </Form>
  )
};

MultiForm.Page = ({ children }) => children;

MultiForm.propTypes = {
  name: PropTypes.string.isRequired,
  onSubmit: PropTypes.func.isRequired,
  hideButtons: PropTypes.bool,
  alwaysUseButtonRows: PropTypes.bool,
  ignoreStoredHistory: PropTypes.bool,
  ignoreStoredValues: PropTypes.bool,
}
